import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Map as A1, CheckSquare as A2, Calendar as A3, Flag as A4, Bell as A5, Users as A6 } from 'lucide-react';

import { useActiveChild } from '../context/ActiveChildContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import ParentViewBanner from '../components/ParentViewBanner';
import CollapsibleSection from '../components/CollapsibleSection';
import JourneyStepSkip from '../components/JourneyStepSkip';
import { Button } from '../components/ui/button';
import {
  GraduationCap, Briefcase, DollarSign, TrendingUp, Target, Award,
  Trophy, ChevronRight, Sparkles, MapPin, Star, Rocket, Shield, Wrench,
  ArrowRight, Clock, CheckCircle, AlertCircle, User, BookOpen,
  BarChart3, Zap, RefreshCw, ExternalLink, Crown, ChevronDown,
  Activity, Layers, Lightbulb, Navigation, Circle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
// import PatriciaPageGuide from '../components/PatriciaPageGuide';  // removed: unified to Sara

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_ICONS = {
  "graduation-cap": GraduationCap, "arrow-right-circle": ArrowRight, "wrench": Wrench,
  "shield": Shield, "rocket": Rocket, "briefcase": Briefcase, "refresh-cw": RefreshCw,
};

const COLOR_MAP = {
  blue: { bg: "from-blue-600/20 to-blue-500/5", border: "border-blue-500/30", text: "text-blue-400", accent: "bg-blue-500", light: "bg-blue-500/15", ring: "ring-blue-500/30", glow: "shadow-blue-500/20", hex: "#3b82f6" },
  purple: { bg: "from-purple-600/20 to-purple-500/5", border: "border-purple-500/30", text: "text-purple-400", accent: "bg-purple-500", light: "bg-purple-500/15", ring: "ring-purple-500/30", glow: "shadow-purple-500/20", hex: "#a855f7" },
  orange: { bg: "from-orange-600/20 to-orange-500/5", border: "border-orange-500/30", text: "text-orange-400", accent: "bg-orange-500", light: "bg-orange-500/15", ring: "ring-orange-500/30", glow: "shadow-orange-500/20", hex: "#f97316" },
  emerald: { bg: "from-emerald-600/20 to-emerald-500/5", border: "border-emerald-500/30", text: "text-emerald-400", accent: "bg-emerald-500", light: "bg-emerald-500/15", ring: "ring-emerald-500/30", glow: "shadow-emerald-500/20", hex: "#10b981" },
  amber: { bg: "from-amber-600/20 to-amber-500/5", border: "border-amber-500/30", text: "text-amber-400", accent: "bg-amber-500", light: "bg-amber-500/15", ring: "ring-amber-500/30", glow: "shadow-amber-500/20", hex: "#f59e0b" },
  cyan: { bg: "from-cyan-600/20 to-cyan-500/5", border: "border-cyan-500/30", text: "text-cyan-400", accent: "bg-cyan-500", light: "bg-cyan-500/15", ring: "ring-cyan-500/30", glow: "shadow-cyan-500/20", hex: "#06b6d4" },
  violet: { bg: "from-violet-600/20 to-violet-500/5", border: "border-violet-500/30", text: "text-violet-400", accent: "bg-violet-500", light: "bg-violet-500/15", ring: "ring-violet-500/30", glow: "shadow-violet-500/20", hex: "#8b5cf6" },
};

const MILESTONE_CATEGORIES = {
  planning: { color: "text-blue-400", bg: "bg-blue-500/15", dot: "bg-blue-500" },
  exam: { color: "text-amber-400", bg: "bg-amber-500/15", dot: "bg-amber-500" },
  financial: { color: "text-emerald-400", bg: "bg-emerald-500/15", dot: "bg-emerald-500" },
  application: { color: "text-purple-400", bg: "bg-purple-500/15", dot: "bg-purple-500" },
  enrollment: { color: "text-cyan-400", bg: "bg-cyan-500/15", dot: "bg-cyan-500" },
  education: { color: "text-indigo-400", bg: "bg-indigo-500/15", dot: "bg-indigo-500" },
  career: { color: "text-orange-400", bg: "bg-orange-500/15", dot: "bg-orange-500" },
  transition: { color: "text-rose-400", bg: "bg-rose-500/15", dot: "bg-rose-500" },
};

function AnimatedNumber({ value, prefix = "", suffix = "", className = "" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!value) return;
    let start = 0;
    const duration = 1200;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (value - start) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <span ref={ref} className={className}>{prefix}{display.toLocaleString()}{suffix}</span>;
}

function ProfileSnapshotCard({ profile }) {
  const navigate = useNavigate();
  const pct = profile.profile_completeness || 0;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800/90 via-slate-800/50 to-slate-900/90 border border-slate-700/40 backdrop-blur-sm" data-testid="profile-snapshot">
      <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-blue-500/8 to-transparent rounded-full -translate-y-12 translate-x-12" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-500/6 to-transparent rounded-full translate-y-8 -translate-x-8" />
      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight">{profile.name}</h2>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {profile.state && <span className="flex items-center gap-1.5 text-slate-300 text-sm"><MapPin className="w-3.5 h-3.5" /> {profile.state}</span>}
              {profile.gpa > 0 && <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-medium"><Star className="w-3.5 h-3.5" /> {profile.gpa} GPA</span>}
              {profile.grade_level && <span className="flex items-center gap-1.5 text-purple-400 text-sm capitalize"><GraduationCap className="w-3.5 h-3.5" /> {profile.grade_level}</span>}
            </div>
          </div>
        </div>
        {profile.career_goal && (
          <div className="p-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20 mb-4">
            <p className="text-blue-300 text-sm flex items-center gap-2"><Target className="w-4 h-4 shrink-0" />Career Goal: <span className="text-white font-semibold">{profile.career_goal}</span></p>
          </div>
        )}
        {(profile.interests?.length > 0 || profile.skills?.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {profile.interests?.map((i, idx) => <span key={`i-${idx}`} className="px-2.5 py-1 bg-blue-500/10 text-blue-300 rounded-lg text-xs border border-blue-500/20">{i}</span>)}
            {profile.skills?.map((s, idx) => <span key={`s-${idx}`} className="px-2.5 py-1 bg-emerald-500/10 text-emerald-300 rounded-lg text-xs border border-emerald-500/20">{s}</span>)}
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-slate-800/60 rounded-full h-2.5 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.2, ease: "easeOut" }}
              className={`h-full rounded-full ${pct >= 80 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : pct >= 50 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-red-500 to-red-400'}`} />
          </div>
          <span className="text-slate-300 text-xs font-mono">{pct}%</span>
        </div>
      </div>
    </motion.div>
  );
}

function PathwayComparisonChart({ pathways }) {
  const maxCost = Math.max(...pathways.map(p => p.estimated_cost_max || 1));
  const maxSalary = Math.max(...pathways.map(p => p.career_outcome?.senior_salary || 1));
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
      className="rounded-2xl bg-gradient-to-br from-slate-800/70 to-slate-900/70 border border-slate-700/40 p-5 backdrop-blur-sm" data-testid="pathway-comparison">
      <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
        <Layers className="w-4 h-4 text-cyan-400" /> At a Glance
      </h3>
      <div className="space-y-4">
        {pathways.map((pw, i) => {
          const c = COLOR_MAP[pw.template.color] || COLOR_MAP.blue;
          const costPct = ((pw.estimated_cost_max || 0) / maxCost) * 100;
          const salaryPct = ((pw.career_outcome?.entry_salary || 0) / maxSalary) * 100;
          const roi = pw.estimated_cost_max > 0 ? ((pw.career_outcome?.entry_salary || 0) / pw.estimated_cost_max).toFixed(1) : "N/A";
          return (
            <motion.div key={pw.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08 }}
              className="group">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-sm font-medium ${c.text}`}>{pw.template.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-slate-300 text-xs">{pw.duration_years}yr</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.light} ${c.text}`}>
                    {i === 0 ? "BEST" : `${pw.fit_score}%`}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] text-slate-300">Cost</span>
                    <span className="text-[10px] text-slate-300 font-mono">${pw.estimated_cost_max > 0 ? `${Math.round(pw.estimated_cost_max / 1000)}K` : 'Free'}</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${costPct}%` }} transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                      className="h-full bg-gradient-to-r from-red-500/60 to-amber-500/60 rounded-full" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] text-slate-300">Entry Salary</span>
                    <span className="text-[10px] text-emerald-400 font-mono">${Math.round((pw.career_outcome?.entry_salary || 0) / 1000)}K</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${salaryPct}%` }} transition={{ duration: 0.8, delay: 0.4 + i * 0.1 }}
                      className="h-full bg-gradient-to-r from-emerald-500/60 to-cyan-500/60 rounded-full" />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

function PathwayCard({ pathway, index, isExpanded, onToggle, isBestMatch, isSelected, onSelect, selecting }) {
  const t = pathway.template;
  const c = COLOR_MAP[t.color] || COLOR_MAP.blue;
  const Icon = PATH_ICONS[t.icon] || GraduationCap;
  const [activeTab, setActiveTab] = useState('timeline');
  const roi = pathway.estimated_cost_max > 0
    ? ((pathway.career_outcome?.entry_salary || 0) / pathway.estimated_cost_max).toFixed(1)
    : null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${c.bg} border ${isBestMatch ? `${c.border} ring-1 ${c.ring} shadow-lg ${c.glow}` : `${c.border}`} transition-all cursor-pointer group`}
      onClick={onToggle} data-testid={`pathway-${pathway.id}`}>
      {isBestMatch && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r" style={{ background: `linear-gradient(90deg, transparent, ${c.hex}, transparent)` }} />
      )}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${c.light} rounded-xl flex items-center justify-center relative`}>
              <Icon className={`w-6 h-6 ${c.text}`} />
              {isBestMatch && (
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Crown className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-bold text-lg">{t.name}</h3>
                {isBestMatch && <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full text-[10px] font-bold tracking-wide">BEST MATCH</span>}
              </div>
              <p className="text-slate-300 text-sm mt-0.5">{t.description}</p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-xl ${c.light} ${c.text} text-sm font-bold tabular-nums`}>
            {pathway.fit_score}%
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="text-center p-2.5 bg-slate-900/40 rounded-xl border border-slate-800/60">
            <Clock className="w-3.5 h-3.5 text-slate-300 mx-auto mb-1" />
            <p className="text-white font-bold text-sm">{pathway.duration_years} yr</p>
            <p className="text-slate-300 text-[10px]">Duration</p>
          </div>
          <div className="text-center p-2.5 bg-slate-900/40 rounded-xl border border-slate-800/60">
            <DollarSign className="w-3.5 h-3.5 text-slate-300 mx-auto mb-1" />
            <p className="text-white font-bold text-sm">
              {pathway.estimated_cost_max === 0 ? 'Free' : `$${Math.round(pathway.estimated_cost_min / 1000)}K-${Math.round(pathway.estimated_cost_max / 1000)}K`}
            </p>
            <p className="text-slate-300 text-[10px]">Est. Cost</p>
          </div>
          <div className="text-center p-2.5 bg-slate-900/40 rounded-xl border border-slate-800/60">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500/50 mx-auto mb-1" />
            <p className="text-emerald-400 font-bold text-sm">${Math.round((pathway.career_outcome?.entry_salary || 0) / 1000)}K</p>
            <p className="text-slate-300 text-[10px]">Entry Salary</p>
          </div>
          <div className="text-center p-2.5 bg-slate-900/40 rounded-xl border border-slate-800/60">
            <Activity className="w-3.5 h-3.5 text-cyan-500/50 mx-auto mb-1" />
            <p className="text-cyan-400 font-bold text-sm">{roi ? `${roi}x` : 'N/A'}</p>
            <p className="text-slate-300 text-[10px]">ROI</p>
          </div>
        </div>

        {/* Why This Fits */}
        {pathway.reasons?.length > 0 && (
          <div className="flex items-start gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-amber-300/80 text-sm">{pathway.reasons.join(' | ')}</p>
          </div>
        )}

        {/* Expanded Section */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
              onClick={(e) => e.stopPropagation()}>
              <div className="mt-4 pt-4 border-t border-white/10">
                {/* Tab Selector */}
                <div className="flex gap-1 mb-4 bg-slate-900/50 rounded-xl p-1">
                  {[
                    { id: 'timeline', label: 'Timeline', icon: Navigation },
                    { id: 'salary', label: 'Salary Growth', icon: BarChart3 },
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? `${c.light} ${c.text}` : 'text-slate-300 hover:text-slate-300'}`}
                      data-testid={`pathway-tab-${tab.id}`}>
                      <tab.icon className="w-3.5 h-3.5" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Timeline Tab */}
                {activeTab === 'timeline' && (
                  <div className="space-y-2.5 relative">
                    <div className={`absolute left-[13px] top-3 bottom-3 w-px ${c.accent} opacity-20`} />
                    {t.milestones.map((m, i) => {
                      const cat = MILESTONE_CATEGORIES[m.category] || MILESTONE_CATEGORIES.planning;
                      return (
                        <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                          className="flex items-start gap-3 relative group/mile">
                          <div className={`w-7 h-7 rounded-full ${cat.bg} flex items-center justify-center shrink-0 z-10 ring-2 ring-slate-900/80`}>
                            <div className={`w-2 h-2 rounded-full ${cat.dot}`} />
                          </div>
                          <div className="flex-1 pb-1">
                            <p className="text-white text-sm font-medium">{m.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-slate-300 text-xs">{m.timeframe}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${cat.bg} ${cat.color} capitalize`}>{m.category}</span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* Salary Tab */}
                {activeTab === 'salary' && (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {[
                        { label: "Entry Level", val: pathway.career_outcome?.entry_salary, color: "from-emerald-600 to-emerald-400" },
                        { label: "Mid Career", val: pathway.career_outcome?.mid_salary, color: "from-cyan-600 to-cyan-400" },
                        { label: "Senior Level", val: pathway.career_outcome?.senior_salary, color: "from-purple-600 to-purple-400" },
                      ].map((s, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-slate-300 text-xs">{s.label}</span>
                            <span className="text-white font-mono text-sm font-bold">${Math.round((s.val || 0) / 1000)}K</span>
                          </div>
                          <div className="bg-slate-800 rounded-full h-3 overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${((s.val || 0) / (pathway.career_outcome?.senior_salary || 100000)) * 100}%` }}
                              transition={{ duration: 0.8, delay: i * 0.15 }}
                              className={`h-full bg-gradient-to-r ${s.color} rounded-full`} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-slate-900/50 rounded-xl border border-slate-800/60">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span className="text-slate-300 text-xs">Growth outlook:</span>
                      <span className="text-emerald-400 text-xs font-bold">{pathway.career_outcome?.growth}</span>
                      <span className="text-slate-300 text-[10px] ml-auto">Source: BLS</span>
                    </div>
                    {pathway.career_outcome?.related?.length > 0 && (
                      <div>
                        <p className="text-slate-300 text-xs mb-2">Related Career Paths</p>
                        <div className="flex flex-wrap gap-1.5">
                          {pathway.career_outcome.related.map((r, i) => (
                            <span key={i} className="px-2.5 py-1 bg-slate-800/80 text-slate-300 rounded-lg text-xs border border-slate-700/50">{r}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between gap-2 mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); if (!isSelected) onSelect(pathway); }}
            disabled={selecting || isSelected}
            data-testid={`select-path-${pathway.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              isSelected
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 cursor-default'
                : 'bg-white/5 text-white border border-white/15 hover:bg-white/10'}`}>
            {isSelected ? <><CheckCircle className="w-3.5 h-3.5" /> This is my path</> : <><Target className="w-3.5 h-3.5" /> Make this my path</>}
          </button>
          <span className="flex items-center text-xs opacity-60" style={{ color: c.hex }}>
            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            {isExpanded ? 'Collapse' : 'Expand journey'}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function ActionCard({ action, index }) {
  const navigate = useNavigate();
  const icons = { "user": User, "award": Award, "trophy": Trophy, "briefcase": Briefcase, "trending-up": TrendingUp, "graduation-cap": GraduationCap };
  const Icon = icons[action.icon] || ChevronRight;
  const isHigh = action.priority === 'high';
  return (
    <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.06 }}
      onClick={() => navigate(action.link)}
      className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left group hover:scale-[1.01] ${isHigh ? 'bg-blue-500/5 border-blue-500/20 hover:border-blue-500/40' : 'bg-slate-800/30 border-slate-700/40 hover:border-slate-600'}`}
      data-testid={`action-${action.icon}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isHigh ? 'bg-blue-500/20' : 'bg-slate-800'}`}>
        <Icon className={`w-4 h-4 ${isHigh ? 'text-blue-400' : 'text-slate-300'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{action.title}</p>
        <p className="text-slate-300 text-xs truncate">{action.description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-400 transition-colors shrink-0" />
    </motion.button>
  );
}

function FundingSnapshot({ totalFunding, totalMatched, topCost, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}
      className="rounded-2xl bg-gradient-to-br from-emerald-600/10 via-slate-800/50 to-cyan-600/10 border border-emerald-500/20 p-5 backdrop-blur-sm" data-testid="funding-snapshot">
      <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-emerald-400" /> Funding Available
      </h3>
      <div className="text-center mb-3">
        <p className="text-3xl font-bold text-emerald-400">
          <AnimatedNumber value={totalFunding} prefix="$" />
        </p>
        <p className="text-slate-300 text-xs mt-1">across {totalMatched} matched scholarships</p>
      </div>
      {topCost > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-slate-300 mb-1">
            <span>Funding vs. Top Path Cost</span>
            <span className="text-emerald-400">{Math.min(Math.round((totalFunding / topCost) * 100), 999)}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((totalFunding / topCost) * 100, 100)}%` }}
              transition={{ duration: 1, delay: 0.5 }}
              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full" />
          </div>
        </div>
      )}
      <Button variant="ghost" className="w-full text-emerald-400 hover:bg-emerald-500/10 text-xs" onClick={() => navigate('/app/scholarships')} data-testid="view-scholarships-btn">
        Browse All Scholarships <ArrowRight className="w-3 h-3 ml-1" />
      </Button>
    </motion.div>
  );
}

function OpportunityMini({ item, type }) {
  const navigate = useNavigate();
  const isScholarship = type === 'scholarship';
  const match = item.match || {};
  return (
    <button onClick={() => navigate(isScholarship ? '/app/scholarships' : '/app/programs')}
      className="w-full p-3 bg-slate-800/30 rounded-xl border border-slate-700/40 hover:border-blue-500/30 transition-all text-left group hover:scale-[1.01]"
      data-testid={`opportunity-${type}-${isScholarship ? item.scholarship_name : item.program_name}`}>
      <div className="flex items-start justify-between mb-1">
        <p className="text-white text-sm font-medium truncate flex-1">{isScholarship ? item.scholarship_name : item.program_name}</p>
        {match.overall_score > 0 && <span className="text-xs text-amber-400 font-bold ml-2">{match.overall_score}%</span>}
      </div>
      <p className="text-slate-300 text-xs truncate">{isScholarship ? item.provider_name : item.organization_name}</p>
      {isScholarship && item.award_amount_max > 0 && <p className="text-emerald-400 text-xs font-mono font-bold mt-1">${item.award_amount_max.toLocaleString()}</p>}
      {match.match_reasons?.[0] && <p className="text-amber-400/60 text-[10px] mt-1 truncate">{match.match_reasons[0]}</p>}
    </button>
  );
}

export default function PathwayDashboard() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPath, setExpandedPath] = useState(null);
  const [selectingPath, setSelectingPath] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Child list + active selection now come from ActiveChildContext (sidebar pill)
  const { children, activeChildId, setActiveChildId } = useActiveChild();
  const activeChild = activeChildId;
  const setActiveChild = setActiveChildId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const childParam = activeChild ? `?child_id=${activeChild}` : '';
        const res = await axios.get(`${API}/pathway/dashboard${childParam}`, getAuthHeader());
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load pathway data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeChild, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const PATH_TYPE_MAP = {
    four_year_college: 'college', community_college_transfer: 'college',
    trade_certification: 'trade', military: 'military',
    entrepreneurship: 'entrepreneurship', direct_to_work: 'workforce', career_transition: 'workforce',
  };

  const selectPath = async (pathway) => {
    setSelectingPath(true);
    try {
      await axios.post(`${API}/paths/select`, {
        path_type: PATH_TYPE_MAP[pathway.id] || 'college',
        institution_name: pathway.template?.name || 'My Pathway',
        major_or_track: pathway.career_outcome?.title || pathway.template?.name,
        lock_status: 'selected', rank: 1,
        notes: `Selected from Build My Plan (fit ${pathway.fit_score}%)`,
      }, getAuthHeader());
      toast.success(`${pathway.template?.name} set as your path!`);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error('Could not save your path. Please try again.');
    } finally { setSelectingPath(false); }
  };

  if (loading) return <AppLayout><div className="flex justify-center py-32"><LoadingSpinner /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-32 text-slate-300">Failed to load dashboard</div></AppLayout>;

  const {
    profile_snapshot: profile, recommended_pathways: pathways, career_outcome: outcome,
    top_scholarships, top_programs, next_actions, saved_counts,
    total_scholarship_funding, total_matched_scholarships, plan_completion, selected_path
  } = data;
  const selectedPathType = selected_path?.path_type;
  const isProf = user?.role === 'professional';

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="plan-arrival"
        eyebrow="Build My Plan"
        title="Turn the decision into dated steps."
        subtitle="A plan is not a wish. This turns everything you have decided into concrete actions with dates, owners and reminders, sequenced so each one is actually possible when it lands."
        accent="#22d3ee"
        second="#34d399"
        ctaLabel="Build my plan"
        narration={"This is Build My Plan. Everything you have decided so far becomes a sequence of dated actions, what to do, by when, and in what order, because some steps only make sense after others. You will get reminders, a weekly check-in, and a clear view of what is overdue versus what is simply ahead. Nothing here is generic advice: every action exists because of something you told us."}
        facts={[
          { icon: A2, head: "Dated actions", sub: "Each one small enough to finish" },
          { icon: A3, head: "Sequenced properly", sub: "Nothing scheduled before it is possible" },
          { icon: A5, head: "Reminders", sub: "A weekly check-in that keeps it moving" }
        ]}
        modules={[
          { icon: A1, label: "Your roadmap", line: "Every step from today to the target, in order", hue: "#22d3ee" },
          { icon: A2, label: "This week", line: "The short list that actually matters right now", hue: "#34d399" },
          { icon: A3, label: "Deadlines", line: "Applications, aid, tests and enrolment dates", hue: "#fbbf24" },
          { icon: A4, label: "Milestones", line: "The moments worth marking on the way", hue: "#a78bfa" },
          { icon: A6, label: "Who helps", line: "People and offices attached to specific steps", hue: "#f472b6" },
          { icon: A5, label: "Check-ins", line: "A weekly nudge, and a record of what you finished", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      {/* Hero */}
      <div className="mb-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight" data-testid="pathway-title">
                {isProf ? 'Career' : 'Future'} <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">Roadmap</span>
              </h1>
              <p className="text-slate-300 mt-2 text-base sm:text-lg">
                {isProf
                  ? "Your step-by-step career transition plan with real salary data and next actions"
                  : user?.role === 'parent'
                    ? `Step-by-step plan for ${activeChild ? children.find(c => (c.id || c.user_id) === activeChild)?.name || children.find(c => (c.id || c.user_id) === activeChild)?.full_name || 'your child' : 'your child'}, from today through career launch`
                    : "Your step-by-step plan from where you are today to your career goal"}
              </p>
            </div>
          </div>
          {/* Parent observer banner, child switching is now via the global sidebar pill */}
          <ParentViewBanner pageLabel="Roadmap" />
        </motion.div>
      </div>

      {/* Sara AI hand-off, Patricia widget removed (assistant unified) */}
      {/* Sara is reachable via the global floating chat in the layout. */}

      {/* Skip option, unlocks the final report step without forcing completion */}
      <JourneyStepSkip step="your_roadmap" nextPath="/app/my-futurepath" nextLabel="My FuturePath Report" />

      {/* Page summary, orients the user and lets them jump down a long page */}
      <div className="mb-6 rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-950/30 to-slate-900/40 backdrop-blur-xl p-5" data-testid="roadmap-summary">
        <h2 className="text-white font-semibold text-lg">Welcome to your Roadmap</h2>
        <p className="text-slate-300 text-sm mt-1 max-w-3xl">
          This is your step-by-step plan from where you are today to your goal. Start with your best-fit
          <strong className="text-white"> pathways</strong>, then work through your <strong className="text-white">next steps</strong>,
          <strong className="text-white"> funding</strong>, and track your <strong className="text-white">progress</strong>. Jump to any section:
        </p>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="roadmap-jump-links">
          {[
            { id: 'rm-pathways', label: 'Pathways' },
            { id: 'section-next-steps', label: 'Next Steps' },
            { id: 'section-funding', label: 'Funding' },
            { id: 'section-progress', label: 'Progress' },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="text-xs px-3 py-1.5 rounded-lg bg-slate-800/70 hover:bg-slate-700 text-slate-200 border border-slate-700/50 transition-colors"
              data-testid={`jump-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Single-column layout, full width, scannable, collapsible secondary sections */}
      <div className="space-y-6">
        {/* Plan Completion Metric */}
        {plan_completion && (
          <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/20 to-slate-900/40 p-5" data-testid="plan-completion">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-white font-semibold text-lg">Your plan is {plan_completion.percent}% complete</h2>
              <span className="text-emerald-300 text-sm font-medium" data-testid="plan-completion-count">{plan_completion.completed}/{plan_completion.total} done</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${plan_completion.percent}%` }} transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" data-testid="plan-completion-bar" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
              {plan_completion.items.map((it) => (
                <button key={it.key} onClick={() => navigate(it.href)} data-testid={`plan-item-${it.key}`}
                  className={`flex items-center gap-2 text-left px-3 py-2 rounded-lg border transition-colors ${
                    it.done ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-slate-700/50 bg-slate-800/40 hover:bg-slate-700/50'}`}>
                  {it.done
                    ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                  <span className={`text-xs ${it.done ? 'text-emerald-100' : 'text-slate-300'}`}>{it.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Profile Snapshot */}
        <ProfileSnapshotCard profile={profile} />

        {/* Career Outcome Banner */}
        {outcome && profile.career_goal && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="rounded-2xl bg-gradient-to-r from-emerald-600/12 via-slate-800/60 to-cyan-600/10 border border-emerald-500/25 p-5 backdrop-blur-sm" data-testid="career-outcome">
            <h3 className="text-base font-bold text-white flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-emerald-400" /> {profile.career_goal}, Career Outlook
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Entry", value: outcome.entry_salary, color: "text-emerald-400" },
                { label: "Mid-Career", value: outcome.mid_salary, color: "text-cyan-400" },
                { label: "Senior", value: outcome.senior_salary, color: "text-purple-400" },
                { label: "Job Growth", value: null, display: outcome.growth, color: "text-amber-400" },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 + i * 0.08 }}
                  className="text-center p-3 bg-slate-900/40 rounded-xl border border-slate-800/60">
                  <p className={`font-bold text-xl sm:text-2xl ${item.color}`}>
                    {item.value ? <AnimatedNumber value={Math.round(item.value / 1000)} prefix="$" suffix="K" /> : item.display}
                  </p>
                  <p className="text-slate-300 text-xs mt-1">{item.label}</p>
                </motion.div>
              ))}
            </div>
            {outcome.related?.length > 0 && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className="text-slate-300 text-xs">Related:</span>
                {outcome.related.map((r, i) => <span key={i} className="px-2 py-0.5 bg-slate-800/80 text-slate-300 rounded-lg text-xs border border-slate-700/40">{r}</span>)}
              </div>
            )}
          </motion.div>
        )}

        {/* Recommended Pathways, primary, always visible */}
        <div id="rm-pathways">
          <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2" data-testid="pathways-heading">
            <Sparkles className="w-5 h-5 text-purple-400" /> Recommended Pathways
          </h2>
          <p className="text-slate-300 text-sm mb-4 max-w-2xl">
            Your best-fit routes, ranked by match. Each card shows the time, cost, and salary outlook,             <strong className="text-slate-300"> tap a card</strong> to open its step-by-step timeline.
          </p>
          <div className="space-y-4" data-testid="pathways-list">
            {pathways.map((pw, i) => (
              <PathwayCard key={pw.id} pathway={pw} index={i}
                isExpanded={expandedPath === pw.id}
                onToggle={() => setExpandedPath(expandedPath === pw.id ? null : pw.id)}
                isBestMatch={i === 0}
                isSelected={selectedPathType === (PATH_TYPE_MAP[pw.id] || pw.template?.path_type)}
                selecting={selectingPath}
                onSelect={selectPath} />
            ))}
          </div>
        </div>

        {/* Next Steps, open by default */}
        <CollapsibleSection icon={Zap} title="Next Steps" accent="text-amber-400" defaultOpen testId="section-next-steps">
          <div className="space-y-2" data-testid="actions-list">
            {next_actions.map((a, i) => <ActionCard key={i} action={a} index={i} />)}
          </div>
        </CollapsibleSection>

        {/* At a Glance Comparison */}
        {pathways.length > 1 && (
          <CollapsibleSection icon={BarChart3} title="Compare Pathways at a Glance" accent="text-cyan-400" testId="section-compare">
            <PathwayComparisonChart pathways={pathways} />
          </CollapsibleSection>
        )}

        {/* Funding Snapshot */}
        {!isProf && total_scholarship_funding > 0 && (
          <CollapsibleSection icon={DollarSign} title="Funding Snapshot" accent="text-emerald-400" testId="section-funding">
            <FundingSnapshot
              totalFunding={total_scholarship_funding}
              totalMatched={total_matched_scholarships || 0}
              topCost={pathways[0]?.estimated_cost_max || 0}
              navigate={navigate}
            />
          </CollapsibleSection>
        )}

        {/* Top Scholarships */}
        {top_scholarships?.length > 0 && !isProf && (
          <CollapsibleSection icon={Award} title="Top Scholarships" accent="text-blue-400" testId="section-scholarships"
            right={<span onClick={(e) => { e.stopPropagation(); navigate('/app/scholarships'); }} className="text-blue-400 text-xs hover:underline cursor-pointer" data-testid="view-all-scholarships">View all</span>}>
            <div className="space-y-2">
              {top_scholarships.slice(0, 3).map((s, i) => <OpportunityMini key={i} item={s} type="scholarship" />)}
            </div>
          </CollapsibleSection>
        )}

        {/* Top Programs */}
        {top_programs?.length > 0 && !isProf && (
          <CollapsibleSection icon={Trophy} title="Top Programs" accent="text-purple-400" testId="section-programs"
            right={<span onClick={(e) => { e.stopPropagation(); navigate('/app/programs'); }} className="text-purple-400 text-xs hover:underline cursor-pointer" data-testid="view-all-programs">View all</span>}>
            <div className="space-y-2">
              {top_programs.slice(0, 3).map((p, i) => <OpportunityMini key={i} item={p} type="program" />)}
            </div>
          </CollapsibleSection>
        )}

        {/* Progress */}
        <CollapsibleSection icon={CheckCircle} title="Your Progress" accent="text-emerald-400" testId="section-progress">
          <div className="space-y-3">
            {[
              { icon: Award, label: "Scholarships Saved", value: saved_counts?.scholarships || 0, color: "text-blue-400" },
              { icon: Trophy, label: "Programs Saved", value: saved_counts?.programs || 0, color: "text-purple-400" },
              { icon: CheckCircle, label: "Profile Complete", value: `${profile.profile_completeness}%`, color: profile.profile_completeness >= 80 ? 'text-emerald-400' : 'text-amber-400' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-slate-300 text-sm flex items-center gap-2"><item.icon className="w-4 h-4" /> {item.label}</span>
                <span className={`font-bold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </AppLayout>
  );
}
